import React, { useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, Euro, Users, CalendarCheck } from "lucide-react";

const GROUPS = [
  { v: "landing_page", l: "Landing page organica" },
  { v: "province", l: "Provincia" },
  { v: "city", l: "Città" },
  { v: "source", l: "Sorgente" },
  { v: "utm_campaign", l: "Campagna" },
];

const eur = (n) => (n || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

const Kpi = ({ label, value, icon: Icon, accent }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-5">
    <div className="flex items-center justify-between"><span className="text-sm font-semibold text-slate-500">{label}</span>
      <span className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: accent || "var(--sc-emerald)" }}><Icon size={18} /></span></div>
    <p className="mt-2 text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit" }}>{value}</p>
  </div>
);

export default function SeoReport() {
  const [group, setGroup] = useState("landing_page");
  const { data, isLoading } = useQuery({ queryKey: ["seo-report", group], queryFn: () => api.get("/admin/seo-report", { params: { group_by: group } }).then((r) => r.data) });
  const rows = data?.rows || [];
  const t = data?.totals || {};

  return (
    <AdminLayout>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div><h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Report Ricavi SEO</h1><p className="text-slate-500">Quali pagine e località organiche generano lead, prove e vendite.</p></div>
        <Select value={group} onValueChange={setGroup}>
          <SelectTrigger className="w-56 bg-white" data-testid="report-group"><SelectValue /></SelectTrigger>
          <SelectContent>{GROUPS.map((g) => <SelectItem key={g.v} value={g.v}>{g.l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {isLoading ? <p className="text-slate-500 mt-6">Caricamento...</p> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <Kpi label="Sessioni organiche" value={t.sessions ?? 0} icon={TrendingUp} />
            <Kpi label="Lead" value={t.leads ?? 0} icon={Users} />
            <Kpi label="Prove richieste" value={t.test_drives ?? 0} icon={CalendarCheck} accent="var(--sc-amber)" />
            <Kpi label="Ricavi (ordini/vinti)" value={eur(t.revenue)} icon={Euro} accent="var(--sc-amber)" />
          </div>

          <div className="mt-6 rounded-2xl bg-white border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="seo-report-table">
                <thead className="bg-slate-50 text-slate-500 text-left"><tr>
                  <th className="px-4 py-3 font-semibold">{GROUPS.find((g) => g.v === group)?.l}</th>
                  <th className="px-4 py-3 font-semibold text-right">Sessioni</th>
                  <th className="px-4 py-3 font-semibold text-right">Lead</th>
                  <th className="px-4 py-3 font-semibold text-right">Qualificati</th>
                  <th className="px-4 py-3 font-semibold text-right">Prove</th>
                  <th className="px-4 py-3 font-semibold text-right">Ordini</th>
                  <th className="px-4 py-3 font-semibold text-right">Ricavi</th>
                  <th className="px-4 py-3 font-semibold text-right">Conv.%</th>
                  <th className="px-4 py-3 font-semibold text-right">€/sessione</th>
                </tr></thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">Nessun dato ancora. I dati appariranno con traffico e lead.</td></tr>}
                  {rows.map((r) => (
                    <tr key={r.key} className="border-t border-slate-100 hover:bg-emerald-50/40">
                      <td className="px-4 py-3 font-medium text-slate-800 truncate max-w-[240px]">{r.key}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{r.sessions || "-"}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">{r.leads}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{r.qualified}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{r.test_drives}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{r.orders}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-800">{eur(r.revenue)}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{r.conversion_rate != null ? `${r.conversion_rate}%` : "-"}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{r.value_per_session != null ? eur(r.value_per_session) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-3">Le sessioni organiche sono stimate dagli eventi page_view. I ricavi si basano sui lead in stato ORDINE/VINTO con valore ordine impostato nel CRM.</p>
        </>
      )}
    </AdminLayout>
  );
}
